/* eslint-disable react-hooks/exhaustive-deps */
import { track } from "@amplitude/analytics-browser";
import { gql, useQuery } from "@apollo/client";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  GetPackOrderDetailQuery,
  GetPackOrderDetailQueryVariables,
  OrdersQuery,
} from "../../__generated__/graphql";
import { postMessage } from "../../core/message";
import { GET_ORDERS } from "../order/page";

const SEND_ORDER_COMPLETION_NOTIFICATION = gql(`
  query SendOrderCompletionNotification($orderId: String) {
    sendOrderCompletionNotification(orderId: $orderId) {
      ok
      error
    }
  }
`);

const GET_PACK_ORDER_DETAIL = gql(`
  query GetPackOrderDetail($orderId: Int!) {
    packOrderDetail(orderId: $orderId) {
      storeName
      storeAddress
      pickUpTimeStart
      pickUpTimeEnd
      confirmTime
    }
  }
`);

const SECOND_ORDER_STORE_REVIEW_SESSION_PREFIX =
  "mahi_second_order_store_review_sent:";

function hasAlreadyRequestedSecondOrderStoreReview(orderIdStr: string): boolean {
  try {
    return (
      sessionStorage.getItem(
        `${SECOND_ORDER_STORE_REVIEW_SESSION_PREFIX}${orderIdStr}`,
      ) === "1"
    );
  } catch {
    return false;
  }
}

function markSecondOrderStoreReviewRequested(orderIdStr: string): void {
  try {
    sessionStorage.setItem(
      `${SECOND_ORDER_STORE_REVIEW_SESSION_PREFIX}${orderIdStr}`,
      "1",
    );
  } catch {
    // 저장 불가(비프라이빗 모드 등) 시에도 리뷰 요청 자체는 시도 가능
  }
}

const NiceResult = () => {
  const location = useLocation();

  // URLSearchParams를 이용해 쿼리 파라미터를 파싱합니다.
  const queryParams = new URLSearchParams(location.search);

  // 특정 쿼리 파라미터 값을 가져옵니다.
  const ok = queryParams.get("ok"); // 예: ?paramName=value
  const messageRaw = queryParams.get("message");
  const messageFromQuery =
    messageRaw !== null && messageRaw !== ""
      ? (() => {
          try {
            return decodeURIComponent(messageRaw);
          } catch {
            return messageRaw;
          }
        })()
      : null;

  const message = messageFromQuery;
  const amount = queryParams.get("amount"); // 예: ?paramName=value
  const orderId = queryParams.get("orderId"); // 예: ?paramName=value
  const packId = queryParams.get("packId"); // 예: ?paramName=value

  useEffect(() => {
    if (ok === "1") {
      track("결제 완료", {
        amount,
        isPack: packId ? true : false,
        packId,
      });
    }
  }, [ok]);
  return ok === "1" ? (
    <PaymentCompleted amount={amount} orderId={orderId} packId={packId} />
  ) : (
    <PaymentFailed message={message} />
  );
};
const PACK_NOTICE_ITEMS = [
  "마감상품 특성상 재고가 남지 않으면 21:30까지 취소될 수 있어요.",
  "21:30 이후에는 자동 확정되며, 취소가 어렵습니다.",
  "반대로 마감상품이 많이 나온 날은 조기 확정될 수도 있어요.",
  "조기 확정 시, 확정된 시점부터 픽업 가능합니다.",
];

const PaymentCompleted = ({
  amount,
  orderId,
  packId,
}: {
  amount: string | null;
  orderId: string | null;
  packId: string | null;
}) => {
  const navigate = useNavigate();

  useQuery(SEND_ORDER_COMPLETION_NOTIFICATION, {
    variables: { orderId },
    skip: !orderId,
  });

  const { data: ordersData } = useQuery<OrdersQuery>(GET_ORDERS, {
    fetchPolicy: "network-only",
    skip: !orderId,
  });

  useEffect(() => {
    if (!orderId || !ordersData?.orders?.length) return;
    if (hasAlreadyRequestedSecondOrderStoreReview(orderId)) return;

    const completedCount = ordersData.orders.filter((o) => !o.isCanceled).length;
    if (completedCount === 2) {
      markSecondOrderStoreReviewRequested(orderId);
      postMessage("REQ_STORE_REVIEW", {});
    }
  }, [orderId, ordersData?.orders]);

  const orderIdNum = orderId ? Number(orderId) : null;
  const { data: packDetailData } = useQuery<
    GetPackOrderDetailQuery,
    GetPackOrderDetailQueryVariables
  >(GET_PACK_ORDER_DETAIL, {
    variables: { orderId: orderIdNum as number },
    skip: !packId || orderIdNum == null || isNaN(orderIdNum),
  });
  const packDetail = packDetailData?.packOrderDetail;
  const isPackComplete = Boolean(packId && packDetail);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
      {/* Success Icon with Bounce and Checkmark Animation */}
      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 bg-[#1562fc] text-white rounded-full flex items-center justify-center animate-bounce-check">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="checkmark"
          >
            <path className="checkmark-path" d="M20 6L9 17l-5-5" />
          </svg>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-black mb-2">
        {isPackComplete
          ? "랜덤팩 예약이 완료되었습니다!"
          : "결제가 완료되었습니다!"}
      </h2>
      <p className="text-gray-600 mb-6">
        {isPackComplete
          ? "픽업 시간과 필독사항을 확인해주세요."
          : "주문이 성공적으로 처리되었습니다."}
      </p>

      {/* Payment Details */}
      <div className="bg-[#f3f3f3] py-3 px-4 rounded-md mb-4 w-full max-w-sm">
        <p className="text-gray-800">
          총 결제 금액:{" "}
          <span className="text-black font-semibold">
            {Number(amount || 0).toLocaleString()}원
          </span>
        </p>
      </div>

      {/* Pack reservation detail */}
      {isPackComplete && packDetail && (
        <>
          <div className="w-full max-w-sm mb-4 rounded-md border border-emerald-200 overflow-hidden">
            <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-200">
              <span className="text-sm font-medium text-emerald-800">
                📍 매장 정보
              </span>
            </div>
            <div className="px-4 py-3 space-y-1.5 text-sm bg-emerald-50/50">
              <p className="text-gray-800">
                <span className="font-medium text-emerald-700">매장 이름:</span>{" "}
                {packDetail.storeName}
              </p>
              <p className="text-gray-800">
                <span className="font-medium text-emerald-700">매장 주소:</span>{" "}
                {packDetail.storeAddress}
              </p>
              <p className="text-gray-800">
                <span className="font-medium text-emerald-700">픽업 시간:</span>{" "}
                {packDetail.pickUpTimeStart} ~ {packDetail.pickUpTimeEnd}
              </p>
              <p className="text-gray-800">
                <span className="font-medium text-emerald-700">확정 시간:</span>{" "}
                {packDetail.confirmTime} (픽업 30분 전)
              </p>
            </div>
          </div>

          <div className="w-full max-w-sm mb-6 rounded-md border border-emerald-200 bg-emerald-50/50 overflow-hidden">
            <div className="px-4 py-2 border-b border-emerald-200 bg-emerald-100/60">
              <span className="text-sm font-medium text-emerald-800">
                🔹 필독사항
              </span>
            </div>
            <ul className="px-4 py-3 space-y-2 text-sm text-gray-700 list-disc list-inside">
              {PACK_NOTICE_ITEMS.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="px-4 pb-3 text-sm font-medium text-emerald-900">
              📛 픽업시간을 지키지 않으면 마감히어로 이용이 제한될 수 있습니다.
            </p>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="space-y-3 w-full max-w-sm">
        <button
          onClick={() => {
            sessionStorage.setItem("scroll-home", "0");
            navigate("/", { replace: true });
          }}
          className="w-full py-3 bg-[#1562fc] text-white rounded-md hover:bg-[#124ab7] transition duration-150"
        >
          홈으로 가기
        </button>
        <button
          onClick={() => {
            sessionStorage.setItem("scroll-order", "0");
            navigate("/order", { replace: true });
          }}
          className="w-full py-3 border border-[#1562fc] text-[#1562fc] rounded-md hover:bg-[#f0f8ff] transition duration-150"
        >
          주문 내역 보기
        </button>
      </div>
    </div>
  );
};

const PaymentFailed = ({ message }: { message: string | null }) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
      {/* Failure Icon with Bounce Animation */}
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 bg-red-500  text-white rounded-full flex items-center justify-center animate-bounce-check">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="failure-icon"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </div>
      </div>

      {/* Payment Failed Message */}
      <h2 className="text-2xl font-semibold text-black mb-2">
        결제가 실패하였습니다!
      </h2>
      <p className="text-gray-600 mb-6 max-w-md whitespace-pre-wrap break-words text-center">
        {message ||
          "결제를 처리하는 중 에러가 발생했습니다. 다시 시도해주세요!"}
      </p>

      {/* Action Buttons */}
      <div className="space-y-3 w-full max-w-sm">
        <button
          onClick={() => navigate("/", { replace: true })}
          className="w-full py-3 bg-red-500 text-white rounded-md hover:bg-[#124ab7] transition duration-150"
        >
          홈으로 가기
        </button>
        <button
          onClick={() => navigate("/order", { replace: true })}
          className="w-full py-3 border border-[#1562fc] text-[#1562fc] rounded-md hover:bg-[#f0f8ff] transition duration-150"
        >
          주문 내역 보기
        </button>
      </div>
    </div>
  );
};

export default NiceResult;
